import { Link } from '@tanstack/react-router';
import { useTranslations } from '@/i18n/translations';
import {
  Body, Card, Eyebrow, I, Icon, Label, LinkUnstyled, Row, Stack, Thumbnail, type Tone,
} from '@leanin/ui';

export type GroupLinkKind = 'network' | 'circle';

const KIND_TONE: Record<GroupLinkKind, Tone> = {
  network: 'navy',
  circle: 'burgundy',
};

// Per-kind fallback glyph + matching ink for the Thumbnail placeholder. The
// inks pair tonally with the fallback tile so the icon never looks pasted on.
const KIND_ICON: Record<GroupLinkKind, { glyph: typeof I.network; ink: string }> = {
  network: { glyph: I.network, ink: 'var(--palette-navy)' },
  circle: { glyph: I.circles, ink: 'var(--primary)' },
};

const KIND_HREF: Record<GroupLinkKind, string> = {
  network: '/networks',
  circle: '/circles',
};

export type GroupLinkListItem = {
  readonly id: string;
  readonly name: string;
  /** One-line subtitle — "X members" for networks, "Role" for circles, etc.
   *  Caller composes whatever fits the surface. */
  readonly meta: string;
  /** Group cover image. When omitted/null the row falls back to a tone-tinted
   *  Thumbnail with the kind's canonical glyph. */
  readonly imageUrl?: string | null;
};

/**
 * Aside-style card listing a member's networks or circles. Each row is a
 * Thumbnail + name + meta + chevron, wrapped in a `LinkUnstyled` to the
 * group detail page.
 *
 * Mirrors the structure of `GroupPeopleListCard` (which uses PersonRow for
 * people) — same composition pattern, different leading visual. Use this
 * any time you need to render a small list of group entities in a card,
 * not the full `GroupCard` tile.
 */
export function GroupLinkListCard({
  title,
  kind,
  groups,
}: {
  title: string;
  kind: GroupLinkKind;
  groups: ReadonlyArray<GroupLinkListItem>;
}) {
  const t = useTranslations('Group');
  if (groups.length === 0) return null;
  const tone = KIND_TONE[kind];
  const { glyph, ink } = KIND_ICON[kind];
  const hrefPrefix = KIND_HREF[kind];

  return (
    <Card pad="default">
      <Stack gap="md">
        <Eyebrow tone="accent">{title}</Eyebrow>
        {groups.map((g) => (
          <LinkUnstyled
            key={g.id}
            href={`${hrefPrefix}/${encodeURIComponent(g.id)}`}
            aria-label={t('card.ariaOpenKind', { kind, name: g.name })}
          >
            <Row gap="md" align="center">
              {g.imageUrl ? (
                <Thumbnail src={g.imageUrl} size={44} />
              ) : (
                <Thumbnail tone={tone} size={44}>
                  <Icon size="md" color={ink}>{glyph}</Icon>
                </Thumbnail>
              )}
              <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                <Label>{g.name}</Label>
                <Body size="sm" color="mute">{g.meta}</Body>
              </Stack>
              <Icon size={14} color="var(--muted-foreground)">{I.arrow}</Icon>
            </Row>
          </LinkUnstyled>
        ))}
      </Stack>
    </Card>
  );
}
